import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { Icon, type IconName } from "../Icon";

type ToastTone = "success" | "danger" | "info";
interface ToastItem { id: number; message: ReactNode; tone: ToastTone }
interface ToastApi { show: (message: ReactNode, tone?: ToastTone) => void }

const ToastContext = createContext<ToastApi | null>(null);
const TOAST_MS = 5000;
const TOAST_ICON: Record<ToastTone, IconName> = { success: "checkCircle", danger: "xCircle", info: "info" };

/** Transient notifications pinned to the bottom-right corner. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback((message: ReactNode, tone: ToastTone = "success") => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-2), { id, message, tone }]);
    setTimeout(() => dismiss(id), TOAST_MS);
  }, [dismiss]);
  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} role="status" className={cx("toast", `toast-${t.tone}`)}>
            <Icon name={TOAST_ICON[t.tone]} size={18} />
            <span>{t.message}</span>
            <button type="button" className="toast-close" aria-label="Đóng" onClick={() => dismiss(t.id)}><Icon name="x" size={14} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
